/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from 'types';

import { ErrorCode } from '@constants/errorCodes';
import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT,
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { GROUPS, SUBSCRIPTIONS } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import {
  BranchByIdInitialState,
  BranchByIdType
} from '@features/branch-management/constants';
import { useLazyGetBranchByIdQuery } from '@features/branch-management/queries';
import { resetGroupState } from '@features/group-management/slices/groupSlice';
import { AddMemberModeType } from '@features/group-management/types';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import { setCurrencyConfig } from '@layouts/main-header/userSlice';
import {
  FeeCategoryDataType,
  FormDataType,
  MemberListType,
  OrgBranchGroupIds
} from '@types';
import { roundTo2 } from '@utils/generic';
import { buildQueryString } from '@utils/queryStringBuilder';
import { getLocalStorageItem, setSessionStorageItem } from '@utils/storage';
import { capitalizeFirstLetter } from '@utils/stringFormat';
import { ValidationError } from 'types/requests';

import { PaymentCollectionType } from '../constants';
import {
  formatFeeConfiguration,
  formatGroupData,
  getAnalyticData,
  getBulkPaymentDiscountRowErrors,
  getCreateGroupPayload,
  getGroupFeeCollectionMethodType,
  groupFormDataDefaultValue,
  isAddNewGroupSaveDisabled,
  isBulkPaymentDiscountIncomplete
} from '../helper';
import { getUpdatedCloneDetails } from '../helpers/createGroup';
import {
  areCustomTermDueDatesAscending,
  CUSTOM_TERM_DUE_DATE_ORDER_ERROR,
  isDateBefore
} from '../helpers/customTerm';
import {
  useCreateGroupMutation,
  useImageUploadMutation,
  useLazyGetGroupByIdQuery
} from '../queries/GroupQuery';

const useCreateGroupForm = () => {
  const [searchParams] = useSearchParams();
  const groupNameFromModal = searchParams.get('name') || '';
  const initialIsAttendanceBased =
    searchParams.get('method') === 'attendance-based';
  const duplicateFrom = searchParams.get('duplicateFrom') || '';

  const branchId = getLocalStorageItem(STORAGE_KEYS.BRANCH_ID);
  const addMemberModeRef = useRef<AddMemberModeType>('File');
  const { createSuccessAlert, createErrorAlert } = useNotify();
  const { checkPermission } = useCheckPermission();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const { orgConfig, rolesAndPermissions, currentSubscription } = useSelector(
    (state: any) => state.user
  );

  const hasGroupAttendanceEditAccess =
    checkPermission([UserPermissionTypes.GROUP_ATTENDANCE_EDIT]) &&
    !!orgConfig?.is_attendance_feature_enabled;

  const [createdGroupId, setCreatedGroupId] = useState<string | null>(null);
  const [isAttendanceBasedFee, setIsAttendanceBasedFee] = useState(
    initialIsAttendanceBased
  );

  const [formData, setFormData] = useState<FormDataType>({
    ...groupFormDataDefaultValue,
    groupName: groupNameFromModal,
    ...(initialIsAttendanceBased && {
      isAttendanceBasedFee: true,
      isAttandanceTrackingEnabled: true,
      isSendAttendanceAlertEnabled: false,
      workingDays: []
    })
  });
  const [memberList, setMemberList] = useState<MemberListType[] | []>([]);
  const [isFeeCategoryEnabled, setIsFeeCategoryEnabled] =
    useState<boolean>(false);
  const [feeCategories, setFeeCategories] = useState<FeeCategoryDataType[]>([]);
  const [branchDetails, setBranchDetails] = useState<BranchByIdType>(
    BranchByIdInitialState
  );
  const [customTermDueDateError, setCustomTermDueDateError] =
    useState<ValidationError>({});
  const [feeDetailsError, setFeeDetailsError] = useState<ValidationError>({});
  const [bulkPaymentDiscountRowErrors, setBulkPaymentDiscountRowErrors] =
    useState<string[]>([]);

  const [getBranchById] = useLazyGetBranchByIdQuery();
  const [getGroupById] = useLazyGetGroupByIdQuery();
  const [uploadImage, { isLoading: uploadImageLoading }] =
    useImageUploadMutation();
  const [createGroup, { isLoading: createGroupLoading }] =
    useCreateGroupMutation();

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;
  const isAmountPaidTillDateEnabled =
    !!orgConfig?.is_amount_paid_till_date_enabled;

  const isLoading = uploadImageLoading || createGroupLoading;

  const getIsDateInPast = (data: string) => {
    const isDueDatePast =
      new Date(new Date(data).setHours(0, 0, 0, 0)) <
      new Date(new Date().setHours(0, 0, 0, 0));
    return isDueDatePast;
  };

  const handleCreateGroup = async (
    imageFilePath?: string
  ): Promise<string | null> => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
      return null;
    }

    if (formData.isCustomTerm && formData.termsList) {
      const pastDueTerm = formData.termsList.find(
        (term) =>
          getIsDateInPast(term.dueDate) &&
          Number(term.dueAmount) !== 0 &&
          term.isPaymentLinkSent === false
      );
      if (pastDueTerm) {
        setBulkPaymentDiscountRowErrors([]);
        setCustomTermDueDateError({
          dueDate: "Due date can't be in the past",
          dueDateRowId: pastDueTerm.id
        });
        return null;
      }
    }

    if (
      formData.isCustomTerm &&
      formData.termsList &&
      !areCustomTermDueDatesAscending(formData.termsList)
    ) {
      const violatingTerm = formData.termsList.find(
        (term, i) =>
          i > 0 &&
          isDateBefore(term.dueDate, formData.termsList![i - 1].dueDate, true)
      );
      setBulkPaymentDiscountRowErrors([]);
      setCustomTermDueDateError({
        dueDate: CUSTOM_TERM_DUE_DATE_ORDER_ERROR,
        dueDateRowId: violatingTerm?.id || ''
      });
      return null;
    }

    if (isBulkPaymentDiscountIncomplete(formData)) {
      setCustomTermDueDateError({});
      setBulkPaymentDiscountRowErrors(
        getBulkPaymentDiscountRowErrors(formData)
      );
      return null;
    }

    const payload = getCreateGroupPayload(
      formData,
      imageFilePath || '',
      memberList,
      feeCategories,
      isFeeCategoryEnabled,
      addMemberModeRef.current === 'File',
      isAmountPaidTillDateEnabled
    );
    const createGroupResponse = await createGroup(payload);
    const mixPanelEventData = getAnalyticData(
      payload.body,
      formData,
      memberList,
      addMemberModeRef.current,
      feeCategories,
      isFeeCategoryEnabled
    );

    if ('data' in createGroupResponse) {
      sendMixpanelEvent(
        isFeeCategoryEnabled
          ? Event.GROUP_WITH_FEE_CATEGORY_CREATED
          : Event.GROUP_CREATION_SUCCESS,
        mixPanelEventData
      );
      createSuccessAlert('Group created successfully');
      setSessionStorageItem(STORAGE_KEYS.NEW_GROUP_NAME, formData.groupName);
      dispatch(resetGroupState());
      if (!formData.isBulkPaymentDiscountEnabled) {
        setFormData((prev) => ({
          ...prev,
          bulkPaymentDiscountData: []
        }));
      }
      const groupId = createGroupResponse.data.id;
      setCreatedGroupId(groupId);
      return groupId;
    }

    if ('error' in createGroupResponse) {
      const error = createGroupResponse.error as ApiError;
      const errorMessage = error.data?.errors?.length
        ? error.data?.errors[0].message
        : error.data?.message;

      if (errorMessage === 'ERR_DUPLICATE_GROUP_NAME') {
        createErrorAlert('Group name already exists!');
      } else {
        createErrorAlert('Error while creating group');
      }
      if (error?.data?.errorCode === ErrorCode.ERR_SUBSCRIPTION_EXPIRED) {
        if (
          rolesAndPermissions.permissions.includes(
            UserPermissionTypes.SUBSCRIPTION_VIEW
          )
        ) {
          navigate(SUBSCRIPTIONS.PLAN_DETAILS);
        } else {
          navigate(GROUPS.HOME);
        }
      }
      sendMixpanelEvent(Event.GROUP_CREATION_ERROR, {
        ...mixPanelEventData,
        error: error.data?.errorCode
      });
      return null;
    }

    return null;
  };

  const onSaveNewGroup = async (): Promise<string | null> => {
    if (formData.groupPhoto?.file) {
      const imageData = new FormData();
      imageData.append('image', formData.groupPhoto.file);
      imageData.append('type', 'group');
      const imageUploadResponse = await uploadImage(imageData);
      if ('data' in imageUploadResponse) {
        sendMixpanelEvent(Event.GROUP_IMAGE_UPLOAD_SUCCESS, {
          group_name: formData.groupName,
          flow: 'New group creation',
          group_type: getGroupFeeCollectionMethodType(
            formData?.isAttendanceBasedFeeEnabled || false
          )
        });
        return handleCreateGroup(imageUploadResponse.data.file_path);
      }

      if ('error' in imageUploadResponse) {
        const error = imageUploadResponse.error as ApiError;
        const errorMessage = error.data?.message
          ? capitalizeFirstLetter(error.data?.message)
          : 'Error while uploading the image';
        createErrorAlert(errorMessage);
        sendMixpanelEvent(Event.GROUP_IMAGE_UPLOAD_ERROR, {
          group_name: formData.groupName,
          error: error.data?.errorCode,
          flow: 'New group creation',
          group_type: getGroupFeeCollectionMethodType(
            formData?.isAttendanceBasedFeeEnabled || false
          )
        });
        return null;
      }

      return null;
    }

    return handleCreateGroup();
  };

  const onInputChange = (field: string, value: any) => {
    if (field === 'amount' && value !== formData.amount) {
      memberList.forEach((member) => {
        if (member.amount === formData.amount) {
          member.amount = value as string;
        }
      });
    }
    setFormData((prev: FormDataType) => ({
      ...prev,
      [field]: value
    }));
  };

  // here the save disable condition
  const isSaveDisabled =
    isAddNewGroupSaveDisabled(
      formData,
      groupMemberMaxAmount,
      groupMemberMinAmount,
      feeCategories,
      isFeeCategoryEnabled
    ) ||
    customTermDueDateError.dueDate ||
    feeDetailsError.endDate ||
    bulkPaymentDiscountRowErrors.some(Boolean);

  const handleGetBranchDetails = async () => {
    const res = await getBranchById({
      params: { organizationId, branchId: branchId || '' },
      queryString: '?include_fee_categories=true&include_payment_stats=false'
    });
    if ('data' in res) {
      setBranchDetails(res.data);
      if (res.data?.currency) {
        dispatch(setCurrencyConfig(res.data.currency));
      }
    } else if ('error' in res) {
      const apiError = res.error as ApiError;
      createErrorAlert(
        apiError.data?.message || 'Error in fetching branch details'
      );
    }
  };

  const updateMemberList = (): MemberListType[] =>
    memberList.map((member) => {
      const updatedFeeCategoryData = member.feeCategoryData?.map((fee) => {
        const updatedCategory = feeCategories.find((cat) => cat.id === fee.id);
        return updatedCategory && !fee.isManuallyUpdated
          ? { ...fee, value: updatedCategory.value }
          : fee;
      });

      const updatedAmount =
        roundTo2(
          updatedFeeCategoryData?.reduce(
            (sum, fee) => sum + Number(fee.value),
            0
          )
        ).toString() || '';

      return {
        ...member,
        feeCategoryData: updatedFeeCategoryData,
        amount: updatedAmount
      };
    });

  const handleAttendanceSwitchClick = (status: boolean) => {
    onInputChange('isAttandanceTrackingEnabled', status);
    sendMixpanelEvent(Event.ATTENDANCE_TRACKING_ENABLED, {
      status,
      page: 'Create group'
    });
    if (status === false) {
      onInputChange('isSendAttendanceAlertEnabled', status);
      onInputChange('workingDays', []);
    }
  };

  const handleDuplicateGroupFetch = async () => {
    const queryString = buildQueryString({
      include_payment_stats: true,
      include_group_stats: true,
      include_payment_details: true,
      include_fee_categories: true
    });
    const params: OrgBranchGroupIds = {
      organizationId,
      branchId: branchId || '',
      groupId: duplicateFrom
    };
    const res = await getGroupById({ params, queryString });

    if ('data' in res && res.data) {
      const dataRes = formatGroupData(res.data);
      const clonedData = getUpdatedCloneDetails(dataRes);

      const wasFeeCategoryEnabled = !!res.data.is_fee_configured;
      if (wasFeeCategoryEnabled) {
        const groupFeeConfig = res.data.group_fee_amount_configuration || [];
        let feeConfiguration;
        if (groupFeeConfig.length > 0) {
          feeConfiguration = formatFeeConfiguration(groupFeeConfig);
        } else {
          const termDetails = res.data.payment_schedule?.term_details || [];
          const firstTermFeeSplit = termDetails[0]?.fee_split || [];
          feeConfiguration = firstTermFeeSplit.map((item: any) => ({
            id: item.branch_fee_category_id,
            value: item.amount || '',
            label: item.category || ''
          }));
        }
        setFeeCategories(feeConfiguration);
      }
      setIsFeeCategoryEnabled(wasFeeCategoryEnabled);

      const isSourceAttendanceBased =
        !!res.data.attendance_config?.is_attendance_based_fee_enabled;
      if (isSourceAttendanceBased) {
        setIsAttendanceBasedFee(true);
      }

      setFormData((prev) => ({
        ...prev,
        ...clonedData
      }));
    }
  };

  useEffect(() => {
    handleGetBranchDetails();
    if (duplicateFrom) {
      handleDuplicateGroupFetch();
    }
  }, []);

  useEffect(() => {
    if (formData.paymentCollectionType === PaymentCollectionType.ONCE) {
      const updatedList = memberList.map((item: MemberListType) => ({
        ...item,
        dueDate: formData.activationDate,
        paymentCollectionType: PaymentCollectionType.ONCE,
        interval: ''
      }));
      setMemberList(updatedList);
    }
  }, [formData.paymentCollectionType]);

  useEffect(() => {
    if (branchDetails.fee_categories?.length) {
      const formattedData = branchDetails.fee_categories.map((item: any) => ({
        id: item.id,
        label: item.category,
        value: '',
        isManuallyUpdated: false
      }));
      if (!duplicateFrom || !isFeeCategoryEnabled) {
        setFeeCategories(formattedData);
      }
    }
  }, [
    JSON.stringify(branchDetails.fee_categories),
    duplicateFrom,
    isFeeCategoryEnabled
  ]);

  useEffect(() => {
    if (feeCategories.length > 0) {
      const data = updateMemberList();
      setMemberList(data);
    }
  }, [feeCategories]);

  return {
    formData,
    setFormData,
    onInputChange,
    memberList,
    isFeeCategoryEnabled,
    setIsFeeCategoryEnabled,
    feeCategories,
    setFeeCategories,
    branchDetails,
    customTermDueDateError,
    setCustomTermDueDateError,
    feeDetailsError,
    setFeeDetailsError,
    bulkPaymentDiscountRowErrors,
    setBulkPaymentDiscountRowErrors,
    isSaveDisabled: isSaveDisabled as boolean,
    isLoading,
    onSaveNewGroup,
    hasGroupAttendanceEditAccess,
    handleAttendanceSwitchClick,
    createdGroupId,
    isAttendanceBasedFee
  };
};

export default useCreateGroupForm;
